import type { AnamnesisSummaryContent, VisitReasonContent } from 'types/anamnesis';
import type { ImmunizationStep } from 'types/immunization';

export type SectionOriginal = {
  key: string;
  name: string;
  text: string;
  sort: number;
  createdAt: string;
  updatedAt: string;
};

export type ICD10Chapter = {
  chapterNumber: string;
  chapterTitle: string;
};

export type ICD10ClassificationGroup = {
  groupStartCode: string;
  groupEndCode: string;
  groupTitle: string;
  chapter?: ICD10Chapter;
};

export type ICD10MorbidityListEntry = {
  morbidityCode: string;
  morbidityTitle: string;
};

export type ICD10MortalityListGroup = {
  mortalityList1GroupCode?: string;
  mortalityList1GroupTitle?: string;
  mortalityList3GroupCode?: string;
  mortalityList3GroupTitle?: string;
};

export type ICD10MortalityListEntry = {
  mortalityList1Code?: string;
  mortalityList1Title?: string;
  mortalityList1Group?: ICD10MortalityListGroup;
  mortalityList2Code?: string;
  mortalityList2Title?: string;
  mortalityList3Code?: string;
  mortalityList3Title?: string;
  mortalityList3Group?: ICD10MortalityListGroup;
  mortalityList4Code?: string;
  mortalityList4Title?: string;
};

export type ICD10CodeEntry = {
  code: string;
  normalizedCode?: string;
  codeWithoutDot?: string;
  title: string;
  threeDigitTitle?: string;
  fourDigitTitle?: string;
  fiveDigitTitle?: string;
  chapter?: ICD10Chapter;
  classificationGroup?: ICD10ClassificationGroup;
  morbidityListEntry?: ICD10MorbidityListEntry;
  mortalityList1Entry?: ICD10MortalityListEntry;
  mortalityList2Entry?: ICD10MortalityListEntry;
  mortalityList3Entry?: ICD10MortalityListEntry;
  mortalityList4Entry?: ICD10MortalityListEntry;
};

export type DiagnosisSelectionChoice = 'selected' | 'deselected';

export type DiagnosedIcdRelationship =
  'duplicate' | 'more_specific' | 'more_general' | 'similar' | 'sibling' | 'unrelated';

export type DiagnosedIcd = {
  id?: number;
  code: string;
  icdCode?: ICD10CodeEntry | null;
  category?: 'D' | 'DD' | 'DDA' | 'acute' | 'long_term' | 'dda' | null;
  sicherheit?:
    | 'verdacht'
    | 'Verdacht'
    | 'gesichert'
    | 'Gesichert'
    | 'verdacht_auf'
    | 'ausschluss_von'
    | 'zustand_nach'
    | 'G'
    | 'A'
    | 'V'
    | 'Z'
    | null;
  seite?: 'links' | 'rechts' | 'beidseitig' | 'keine_angabe' | null;
  isSelfAnamnesis: boolean;
  selected?: boolean;
  selectable?: boolean;
  relationship?: DiagnosedIcdRelationship | null;
  matchedExistingCode?: string | null;
  matchedExistingIsDauerdiagnose?: boolean | null;
};

export type EbmExclusionScope = 'Sitzung' | 'Behandlungsfall';

export type EbmExclusion = {
  conflictingCode: string;
  scope: EbmExclusionScope;
  reasonCode?: string | null;
};

export const EBM03230 = '03230';
export const EBM03362 = '03362';
export const EBM03360 = '03360';

export type EbmProposal = {
  description: string;
  excluded?: boolean;
  exclusions?: EbmExclusion[] | null;
  selected?: boolean;
} & (
  | { code: typeof EBM03230; multiplier: number; durationMinutes: number }
  | { code: typeof EBM03362; inclusionReason: string }
  | { code: typeof EBM03360 }
);

export type Patient = {
  id: string;
  fullname: string;
  birthdate: string;
  medatixxId: string;
};

export type SuggestionItem = {
  id: string;
  text: string;
  anchor: string | null;
};

export type SuggestionsData = {
  anamnesis: SuggestionItem[] | null;
  findings: SuggestionItem[] | null;
  procedure: SuggestionItem[] | null;
  therapy: SuggestionItem[] | null;
};

export type ResultData = {
  id: number;
  interaction: Interaction;
  transcripts?: TranscriptData[];
  transcriptDeleted?: boolean;
  timestamp: string;
  documentId: string;
  language: string;
  exportStatus?: ExportStatus | null;
  durationSeconds: number;
  anamnesis: string;
  findings: string;
  diagnosis: string;
  therapy: string;
  procedure: string;
  comment?: string;
  diagnosedIcds?: DiagnosedIcd[] | null;
  ebmProposals?: EbmProposal[] | null;
  visitReason?: VisitReasonContent;
  anamnesisSummary?: AnamnesisSummaryContent;
  patient?: Patient;
  vitalParameters?: VitalParameters;
  suggestions?: SuggestionsData | null;
  suggestionsFailed?: boolean;
};

export type VitalParameters = {
  systolicMmhg?: number | null;
  diastolicMmhg?: number | null;
  pulseBpm?: number | null;
  pulsePhase?: string | null;
  temperatureC?: number | null;
  waistCircumferenceCm?: number | null;
  respiratoryRateBpm?: number | null;
  spo2Percent?: number | null;
  hipCircumferenceCm?: number | null;
  headCircumferenceCm?: number | null;
  peakFlowLMin?: number | null;
  fev1L?: number | null;
  heightCm?: number | null;
  weightKg?: number | null;
};

export type TranscriptData = {
  id: number;
  recordingId?: string;
  text: string;
  createdAt: string;
  interaction?: number;
  result?: number;
  user?: number;
};

export enum ResultTemplateKey {
  Anamnesis = 'anamnesis',
  Findings = 'findings',
  Diagnosis = 'diagnosis',
  Therapy = 'therapy',
  Procedure = 'procedure',
  Comment = 'comment',
  DiagnosedIcd = 'diagnosed-icds',
  EbmCodes = 'ebm-codes',
  VitalParameters = 'vital-parameters',
  SelfAnamnesis = 'self-anamnesis',
  Immunization = 'immunization',
}

export const SUGGESTION_FIELD: Partial<Record<ResultTemplateKey, keyof SuggestionsData>> = {
  [ResultTemplateKey.Anamnesis]: 'anamnesis',
  [ResultTemplateKey.Findings]: 'findings',
  [ResultTemplateKey.Procedure]: 'procedure',
  [ResultTemplateKey.Therapy]: 'therapy',
};

export type Interaction = {
  id: number;
  interactionId: string;
  startedAt: string;
  wssUrl: string;
  withoutRecording: boolean;
};

export enum ExportStatusValue {
  Success = 'SUCCESS',
  Failure = 'FAILURE',
  Error = 'ERROR',
  NoStatus = 'NO_STATUS',
  Pending = 'PENDING',
  Progress = 'PROGRESS',
  Retry = 'RETRY',
  Processing = 'PENDING,RETRY',
  Failed = 'FAILURE,ERROR',
  NotExported = 'FAILURE,ERROR,None',
  NoStatusFilter = 'NO_STATUS,None',
}

// Document entry types returned in ExportStatus.result from the async export endpoint.
// snake_case fields are camelCased by the backend before sending.

type TextDocumentEntryType =
  | ResultTemplateKey.Anamnesis
  | ResultTemplateKey.Findings
  | ResultTemplateKey.Therapy
  | ResultTemplateKey.Procedure
  | ResultTemplateKey.Comment
  | ResultTemplateKey.SelfAnamnesis;

export type TextDocumentEntry = {
  type: TextDocumentEntryType;
  date: string | null;
  text: string;
  successfulTransmitted: boolean | null;
};

export type DiagnosisEntryDetail = {
  category: DiagnosedIcd['category'];
  searchText: string;
  sicherheit: DiagnosedIcd['sicherheit'];
  seite: DiagnosedIcd['seite'];
  label: string | null;
};

export type DiagnosisDocumentEntry = {
  type: ResultTemplateKey.DiagnosedIcd;
  date: string | null;
  text: DiagnosisEntryDetail;
  successfulTransmitted: boolean | null;
};

export type EbmDocumentEntry = {
  type: ResultTemplateKey.EbmCodes;
  date: string | null;
  text: EbmProposal;
  successfulTransmitted: boolean | null;
};

export type VitalParametersDocumentEntry = {
  type: ResultTemplateKey.VitalParameters;
  date: string | null;
  text: VitalParameters;
  successfulTransmitted: boolean | null;
};

export type ImmunizationDocumentEntry = {
  type: ResultTemplateKey.Immunization;
  date: string;
  text: { immunizationId: number; vaccineName: string; gopCode: string };
  successfulTransmitted: boolean | null;
  transmittedSteps: ImmunizationStep[];
};

export type DocumentEntry =
  | TextDocumentEntry
  | DiagnosisDocumentEntry
  | EbmDocumentEntry
  | VitalParametersDocumentEntry
  | ImmunizationDocumentEntry;

export type ExportStatus = {
  id: number;
  status: ExportStatusValue;
  result: DocumentEntry[] | null;
  taskId: string;
  dateDone: string | null;
};

export type BriefResultData = {
  id: number;
  timestamp: string;
  durationSeconds: number;
  exportStatus?: ExportStatus | null;
  patient?: Patient | null;
  visitReasonShort?: string | null;
  chiefComplaint?: string | null;
};

export type AllResultsResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  failedCount: number;
  notExportedCount: number;
  results: BriefResultData[];
};
